import React, { useState, useEffect } from 'react';
import { FaListAlt, FaChevronLeft } from 'react-icons/fa';
import { reverseGeocode } from '../utils/reverseGeocode';
import { loadAoiPoints } from '../utils/aoiLoader';
import { useDownloadMap } from '../utils/useDownloadMap';

function formatScenarioId(id) {
  const match = id.match(/^Mw(\d+)_C(\d+)$/);
  if (!match) return id;
  const mag = (parseInt(match[1], 10) / 100).toFixed(1);
  const scenario = parseInt(match[2], 10);
  return `M ${mag} Scenario: ${scenario}`;
}

// Desktop/tablet floating collapsible panel. Mobile uses ScenarioList
// (content-only) inside ControlPanel's bottom sheet instead — see App.js.
export default function ScenarioPanel({ results, selected, onSelect, searchPin, maxCoastal }) {
  const [collapsed, setCollapsed] = useState(() => window.innerWidth <= 1100);
  const [placeLabel, setPlaceLabel] = useState('');
  const [aoiPoints, setAoiPoints] = useState(null);
  const { downloading, error: downloadError, download } = useDownloadMap(selected);

  const active = results.find(r => r.id === selected);

  // Load AOI points for the selected scenario
  useEffect(() => {
    if (!selected) { setAoiPoints(null); return; }
    let cancelled = false;
    loadAoiPoints(selected)
      .then(pts => { if (!cancelled) setAoiPoints(pts); })
      .catch(() => { if (!cancelled) setAoiPoints([]); });
    return () => { cancelled = true; };
  }, [selected]);

  // Resolve place name for the active pin
  useEffect(() => {
    if (!searchPin) { setPlaceLabel(''); return; }
    // USGS pick: place string already available
    if (searchPin.place) { setPlaceLabel(searchPin.place); return; }
    // Map click or typed coords: reverse-geocode
    let cancelled = false;
    setPlaceLabel('Locating…');
    reverseGeocode(searchPin.lat, searchPin.lng)
      .then(label => { if (!cancelled) setPlaceLabel(label); })
      .catch(() => { if (!cancelled) setPlaceLabel('—'); });
    return () => { cancelled = true; };
  }, [searchPin]);

  if (!results.length) return null;

  if (collapsed) {
    return (
      <div
        className="panel-icon-btn scenario-icon-btn"
        onClick={() => setCollapsed(false)}
        title="Nearest Scenarios"
      >
        <FaListAlt size={16} color="#555" />
      </div>
    );
  }

  return (
    <div className="panel scenario-panel">
      <div className="panel-header">
        <span className="panel-label">
          <FaListAlt size={9} style={{ marginRight: 5, verticalAlign: 'middle' }} />
          Nearest Scenarios
        </span>
        <button
          className="panel-collapse-btn"
          onClick={() => setCollapsed(true)}
          title="Collapse"
        >
          <FaChevronLeft size={9} />
        </button>
      </div>

      {active && (() => {
        const peakWave = aoiPoints?.length
          ? Math.max(...aoiPoints.map(p => p.h))
          : null;
        return (
          <div className="active-summary">
            <div className="as-top">
              <span className="as-mag">M {active.magnitude.toFixed(1)}</span>
              <span className="as-id">Scenario {parseInt(active.id.slice(-3), 10)}</span>
            </div>
            <div className="as-source">{placeLabel || '—'}</div>
            <div className="as-stats">
              <div className="as-stat"><label>Distance</label><b>{active.distanceKm} km</b></div>
              <div className="as-stat"><label>Peak wave</label>
                <b style={{ color: '#8b0000' }}>{peakWave != null ? peakWave.toFixed(1) + ' m' : '—'}</b></div>
              <div className="as-stat"><label>Max coastal</label>
                <b style={{ color: '#8b0000' }}>{maxCoastal != null ? maxCoastal.toFixed(1) + ' m' : '—'}</b></div>
            </div>
          </div>
        );
      })()}

      <ul className="scenario-list">
        {results.map(r => (
          <li
            key={r.id}
            className={`scenario-item${r.id === selected ? ' active' : ''}`}
            onClick={() => onSelect(r.id)}
          >
            <span className="scenario-id">{formatScenarioId(r.id)}</span>
            <span className="scenario-dist">{r.distanceKm} km</span>
          </li>
        ))}
      </ul>

      {selected && (
        <>
          <button
            className="download-btn"
            onClick={download}
            disabled={downloading}
          >
            {downloading ? 'Downloading…' : '↓ Download Map'}
          </button>
          {downloadError && (
            <div className="download-error">{downloadError}</div>
          )}
        </>
      )}
    </div>
  );
}
